/* Derived view-model for the terminal route.
 * Ported from the artifact's vals_terminal(), which closed over component state
 * and the global keydown handler. */
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type FormEvent,
  type KeyboardEvent,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { CLUSTERS, SECTIONS, SOURCES, panelView, sessionRows, type ClusterId } from '@/data/wft';
import { setSignedIn } from '@/lib/prefs';
import {
  cycleDensity as cycleDensityPref,
  toggleTheme as toggleThemePref,
  useDensity,
  useTheme,
} from '@/lib/theme-store';

const RAIL_ORDER: ClusterId[] = ['tape', 'context', 'engine', 'desk', 'system'];
const LEADER_TIMEOUT = 1200;
const MAX_MATCHES = 6;

export function useTerminalVals(now: Date | null) {
  const navigate = useNavigate();
  const theme = useTheme();
  const density = useDensity();

  const [sectionId, setSectionId] = useState('cockpit');
  const [railOpen, setRailOpen] = useState(true);
  const [cmd, setCmd] = useState('');

  const current = useMemo(
    () => SECTIONS.find((x) => x.id === sectionId) ?? SECTIONS[0],
    [sectionId]
  );

  const matches = useMemo(() => {
    const q = cmd.trim().toLowerCase();
    if (!q) return [];
    return SECTIONS.filter(
      (x) => x.mnemonic.toLowerCase().startsWith(q) || x.title.toLowerCase().includes(q)
    )
      .slice(0, MAX_MATCHES)
      .map((x) => ({
        mnemonic: x.mnemonic,
        title: x.title,
        purpose: x.purpose,
        go: () => {
          setSectionId(x.id);
          setCmd('');
        },
      }));
  }, [cmd]);

  // Global keymap. The `g` leader arms for LEADER_TIMEOUT, after which the next
  // key jumps to the section declaring it.
  useEffect(() => {
    let leader = false;
    let leaderTimer: ReturnType<typeof setTimeout> | undefined;

    const onKey = (e: globalThis.KeyboardEvent) => {
      const el = document.activeElement as HTMLElement | null;
      const typing =
        !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable);

      if (e.ctrlKey && e.shiftKey && (e.key === 'T' || e.key === 't')) {
        e.preventDefault();
        toggleThemePref();
        return;
      }
      if (e.ctrlKey && e.shiftKey && (e.key === 'D' || e.key === 'd')) {
        e.preventDefault();
        cycleDensityPref();
        return;
      }
      if (e.ctrlKey && !e.shiftKey && (e.key === 'b' || e.key === 'B')) {
        e.preventDefault();
        setRailOpen((v) => !v);
        return;
      }
      if (typing) return;

      if (leader) {
        leader = false;
        clearTimeout(leaderTimer);
        const hit = SECTIONS.find((s) => s.leader === e.key.toLowerCase());
        if (hit) {
          e.preventDefault();
          setSectionId(hit.id);
        }
        return;
      }
      if (e.key === 'g' && !e.ctrlKey && !e.metaKey && !e.altKey) {
        leader = true;
        clearTimeout(leaderTimer);
        leaderTimer = setTimeout(() => {
          leader = false;
        }, LEADER_TIMEOUT);
      }
    };

    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      clearTimeout(leaderTimer);
    };
  }, []);

  const signOut = useCallback(() => {
    setSignedIn(false);
    navigate('/');
  }, [navigate]);

  return {
    sessions: sessionRows(now),
    sources: SOURCES,
    railGroups: RAIL_ORDER.map((c) => ({
      label: CLUSTERS[c].label,
      items: SECTIONS.filter((x) => x.cluster === c).map((x) => {
        const active = x.id === sectionId;
        return {
          mnemonic: x.mnemonic,
          title: x.title,
          tip: `${x.title} — ${x.purpose}  (${x.mnemonic}, g${x.leader})`,
          nc: x.live ? '' : 'NC',
          active,
          edge: active ? 'var(--wft-accent)' : 'transparent',
          bg: active ? 'var(--wft-accent-bg)' : 'transparent',
          mnemonicColor: active ? 'var(--wft-accent)' : 'var(--wft-ink-3)',
          titleColor: active ? 'var(--wft-accent)' : 'var(--wft-ink-1)',
          go: () => setSectionId(x.id),
        };
      }),
    })),
    current: {
      mnemonic: current.mnemonic,
      title: current.title,
      purpose: current.purpose,
      panelCount: `${current.panels.length} panels · ${CLUSTERS[current.cluster].label}`,
      panels: current.panels.map((p, i) => panelView(p, i)),
    },
    railOpen,
    railWidth: railOpen ? '208px' : '44px',
    toggleRail: () => setRailOpen((v) => !v),
    toggleTheme: toggleThemePref,
    cycleDensity: cycleDensityPref,
    themeLabel: theme,
    densityLabel: density,
    cmd,
    onCmd: (e: FormEvent<HTMLInputElement>) => setCmd(e.currentTarget.value),
    onCmdKey: (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' && matches.length) {
        e.preventDefault();
        matches[0].go();
      }
      if (e.key === 'Escape') setCmd('');
    },
    matches,
    hasMatches: matches.length > 0,
    signOut,
    utc: now
      ? `${new Intl.DateTimeFormat('en-GB', {
          timeZone: 'UTC',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false,
        }).format(now)} UTC`
      : '—',
  };
}

export type TerminalVals = ReturnType<typeof useTerminalVals>;
